import { useLayoutEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import {
  Box,
  Button,
  Divider,
  Link,
  Paper,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';

import {
  createReservationFormSchema,
  ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';

import { usePortalContext } from '@/contexts/PortalProvider';
import { useReservationFormMachines } from './ReservationFormMachinesContext';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';

import type { LevelOfCareContext } from './machines/levelOfCare.machine';
import { TransportationAttribute } from '@/data/models/transportation-attribute.model';

import { selectPassengerClientData } from '@/data/client.slice';
import * as lookupSlice from '@/data/lookup.slice';

import PassengerBenefits from '../home/passengers/PassengerBenefits';
import { TripMachineCountSync } from './fields/TripMachineCountSync';
import { useFieldActorSnapshot } from './hooks/useFieldActorSnapshot';
import { useDebouncedReservationActionSelectors } from './hooks/useDebouncedReservationActionSelectors';
import { mergeAggregatedWithLiveForm } from './utils/getAggregatedFormValues';
import { useReservationFieldEditValidation } from './hooks/useReservationFieldEditValidation';
import { MemberFormOtherInputs } from './fields/MemberFormOtherInputs';
import { PhoneInputField } from './fields/PhoneInputField';
import Checkbox from '@/components/forms/Checkbox';
import ChipSelectMenu from '@/components/forms/ChipSelectMenu';
import BookTripSupport from '@/components/member/reservation/BookTripSupport';

const PREFIX = 'ReservationMemberForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  benefits: `${PREFIX}-benefits`,
  phoneNumberFields: `${PREFIX}-phoneNumberFields`,
  subctrl: `${PREFIX}-subctrl`,
  names: `${PREFIX}-names`,
  name: `${PREFIX}-name`,
  attributes: `${PREFIX}-attributes`,
  previous: `${PREFIX}-previous`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-around',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },
  [`& .${classes.benefits}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 550,
  },
  [`& .${classes.phoneNumberFields}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 188,
    },
  },
  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(2),
  },
  [`& .${classes.names}`]: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.name}`]: {
    flexGrow: 1,
  },
  [`& .${classes.attributes}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'end',
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    letterSpacing: 1.5,
    lineHeight: '157%',
    marginLeft: theme.spacing(-1),
  },
}));

interface ReservationMemberFormProps {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  transportationAttributes: TransportationAttribute[];
  onNext: (evt: any) => void;
  onPrevious: (evt: any) => void;
}

const ReservationMemberForm = ({
  form,
  transportationAttributes,
  onNext,
  onPrevious,
}: ReservationMemberFormProps) => {
  const t = useCommonTranslation();
  const { isPortal, isMemberPortal } = usePortalContext();
  const theme = useTheme();
  const { mainActor, mainSnapshot, fieldRefs } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const isNonAmbulatoryByIndex = (levelOfCareSnap.context as LevelOfCareContext)
    .isNonAmbulatoryByIndex;
  const reservationFormSchema = useMemo(
    () => createReservationFormSchema(null, t, isNonAmbulatoryByIndex),
    [t, isNonAmbulatoryByIndex],
  );

  const passengerClientData = useAppSelector(selectPassengerClientData);
  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );
  const driverNoteTypes = useAppSelector(lookupSlice.selectDriverNoteTypes);

  const handleSaveAsDraft = () => {
    const snap = mainActor.getSnapshot();
    const mergedValues = mergeAggregatedWithLiveForm(
      snap.context,
      form.getValues(),
    );
    mainActor.send({ type: 'SAVE_AS_DRAFT', formValues: mergedValues });
  };

  const handleOverrideOpen = () => {
    mainActor.send({ type: 'OVERRIDE_OPEN' });
  };

  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  const watchedForm = form.watch();

  useReservationFieldEditValidation(form);

  const mergedValues = useMemo(() => {
    return mergeAggregatedWithLiveForm(mainSnapshot.context, form.getValues());
  }, [mainSnapshot.context, watchedForm]);

  useLayoutEffect(() => {
    const existing = mainActor.getSnapshot().context.tripLegMergedValues;
    if (existing !== undefined && isEqual(existing, mergedValues)) {
      return;
    }
    mainActor.send({
      type: 'TRIP_LEG_MERGED_VALUES',
      mergedValues,
    });
  }, [mainActor, mergedValues]);

  const { requestOverrideButtonDisabled, saveAsDraftButtonDisabled } =
    useDebouncedReservationActionSelectors(mainSnapshot);

  const handleNextStep = (evt: any) => {
    if (isSubmitting) return;
    if (!reservationFormSchema.safeParse(mergedValues).success) return;
    onNext?.(evt);
  };

  return (
    <FormProvider {...form}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <TripMachineCountSync />
        <StyledBox
          className={classes.container}
          component="form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleNextStep(evt);
          }}>
          <Paper elevation={2} className={classes.form}>
            <Link
              color="primary"
              className={classes.previous}
              href="#"
              onClick={(evt) => onPrevious(evt)}
              underline="hover">
              <ChevronLeftOutlinedIcon />
              {t('common.back')}
            </Link>
            <Typography className={classes.title}>
              {t('reservation.memberDetails')}
            </Typography>
            <PhoneInputField
              tripField="MemberHomePhone"
              label={t('reservation.homePhone')}
              className={classes.phoneNumberFields}
            />
            <PhoneInputField
              tripField="MemberMobilePhone"
              label={t('reservation.mobilePhone')}
              className={classes.phoneNumberFields}
            />
            {!isPortal && (
              <Checkbox
                name="AllowText"
                label={t('reservation.allowText')}
                control={form.control as any}
              />
            )}
            <Divider />
            <ChipSelectMenu
              label={t('reservation.passengerNotes')}
              name="PassengerNoteTypes"
              values={passengerNoteTypes}
              valueAttr="tripLegPassengerNoteTypeBC"
              labelAttr="tripLegPassengerNoteTypeName"
              required={false}
              control={form.control as any}
            />
            <ChipSelectMenu
              label={t('reservation.driverNotes')}
              name="DriverNoteTypes"
              values={driverNoteTypes}
              valueAttr="tripLegDriverNoteTypeBC"
              labelAttr="tripLegDriverNoteTypeName"
              required={false}
              control={form.control as any}
            />
            <MemberFormOtherInputs
              form={form}
              transportationAttributes={transportationAttributes}
            />
            <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  isSubmitting ||
                  !reservationFormSchema.safeParse(mergedValues).success
                }
                data-fs-element="CreateReservationStep2NextButton">
                {t('reservation.next')}
              </Button>
              {!isPortal && (
                <>
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={saveAsDraftButtonDisabled}
                    onClick={handleSaveAsDraft}
                    data-fs-element="CreateReservationSaveAsDraftButton">
                    {t('reservation.saveAsDraft')}
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={requestOverrideButtonDisabled}
                    onClick={handleOverrideOpen}
                    data-fs-element="CreateReservationRequestOverrideStartButton">
                    {t('reservation.requestOverride')}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
          {isMemberPortal ? (
            <BookTripSupport />
          ) : (
            <Box className={classes.benefits}>
              <PassengerBenefits
                appointmentDateTime={
                  form.getValues('trips.0.appointmentDateTime') ?? undefined
                }
                servicePlans={passengerClientData?.servicePlans || []}
              />
            </Box>
          )}
        </StyledBox>
      </LocalizationProvider>
    </FormProvider>
  );
};

export { ReservationMemberForm, type ReservationMemberFormProps };
